/**
 * Bluebird design tokens.
 *
 * The September 2026 review adds warm cream surfaces and Colorado red accents.
 * Original geometry was MEASURED off the rendered comps by
 * `script/extract_tokens.mjs` (getComputedStyle at 1440px and 390px) and
 * cross-checked against the comp source. The census it produced is committed at
 * `docs/redesign/tokens/computed-census.json` / `.md`.
 *
 * The comp is `clamp()`-based throughout, so the fluid values below are kept as
 * their literal clamp expressions rather than as the resolved pixel numbers.
 * Resolving them would pin the design to one viewport, which is the opposite of
 * what the comp does.
 */

/** Colorado flag palette, as it actually renders. */
export const color = {
  /** #002868 · rgb(0,40,104) — Colorado blue. Headers, dark bands, links. */
  blue: "#002868",
  /** Hover state for a filled blue button. */
  blueHover: "#003C93",
  /** #FFD700 — Colorado gold. Primary CTA fill, accents, the rotating word. */
  gold: "#FFD700",
  /** Hover state for a filled gold button. */
  goldHover: "#FFE24D",
  /** Colorado red: flag mark, welcoming actions, and small accents. */
  red: "#BF0A30",
  /**
   * Availability badge red. Was a muted maroon #8A2033; Aaron's standing
   * direction (docs/design/AARON-FEEDBACK-2026-09-07.md) is that red on this
   * site means Colorado flag red, same as `red` above.
   */
  soldRed: "#BF0A30",

  /** #131A2C — body ink. The most common color on the page (77 elements). */
  ink: "#131A2C",
  /** Secondary body copy on light ground. */
  inkMuted: "#3B4358",
  /** Tertiary / meta copy on light ground. */
  slate: "#5A6580",
  /** Mono eyebrow + caption gray. */
  slateLight: "#8895AE",
  /** Italic placeholder copy in the testimonial cards. */
  quoteGray: "#6B7A93",
  /** Mono label ink inside the hover overlay of a bike card. */
  overlayInk: "#33415C",
  /** Body copy on the gold savings band — a warm dark that reads on #FFD700. */
  onGold: "#3A3106",

  white: "#FFFFFF",
  /** Body copy on a blue band. */
  onBlue: "#C7D0E4",
  /** Footer body copy — dimmer than onBlue. */
  footerText: "#8FA0C6",

  /** Hairline under the promise strip. */
  border: "#E2E7F1",
  /** Testimonial card border. */
  borderCard: "#D8E0EE",
  /** Testimonial card hover border. */
  borderCardHover: "#B9C6DE",
  /** Divider inside a testimonial card + its 1px bottom shadow. */
  borderFaint: "#EDF1F8",
  /** Border of the `empty` badge. */
  borderBadge: "#DCE3EF",

  /** Map section ground + the light half of the placeholder hatch. */
  surface: "#FFF8EE",
  /** Fill of the `empty` badge. */
  surfaceBadge: "#FFF3DE",
  /** Dark half of the light placeholder hatch. */
  hatchLight: "#E2E7F1",
  /** Light half of the bike-card hover-overlay hatch. */
  hatchOverlayLight: "#D9E2F2",
  /** Dark half of the bike-card hover-overlay hatch. */
  hatchOverlayDark: "#C9D5EC",
  /** Light half of the hatch used on blue ground. */
  hatchBlueLight: "#0A3578",
  /** Dark half of the hatch used on blue ground. */
  hatchBlueDark: "#0D3F8C",
} as const;

/** Font stacks. Archivo is self-hosted — see app/frontend/styles/archivo.css. */
export const font = {
  sans: "'Archivo', Helvetica, sans-serif",
  mono: "ui-monospace, Menlo, monospace",
  /** Used only for the oversized opening quote mark on a testimonial card. */
  serif: "Georgia, serif",
} as const;

/**
 * Fluid type, kept as the comp's own clamp() expressions.
 * Names describe the role, not the size.
 */
export const type = {
  /** The rotating hero headline. Resolves 42px → 88px. */
  hero: "clamp(42px,7vw,88px)",
  /**
   * The sub-page hero headline (Service, About, and the bike detail page).
   * 38px → 76px, measured from the Service and About comps.
   *
   * Promoted out of Chrome.tsx 2026-08-16. It lived inline there, so
   * Bikes/Show.tsx — which has the same kind of headline but does not use
   * SubHero — grew its own `clamp(34px,5vw,64px)` instead, landing on no step
   * of the comp's scale at either end. A measured value with no name gets
   * re-invented by the next page that needs it.
   */
  subHero: "clamp(38px,6vw,76px)",
  /** Section headings ("The bikes", "What riders say"). 28px → 40px. */
  h2: "clamp(28px,3.6vw,40px)",
  /** The savings-band heading. 26px → 36px. */
  h2Tight: "clamp(26px,3.2vw,36px)",
  /** The visit-card heading. 24px → 32px. */
  h3: "clamp(24px,3vw,32px)",
  /** The big dollar figures on the savings band. 38px → 60px. */
  figure: "clamp(38px,5.5vw,60px)",
  /** The promise-strip lede. 19px → 24px. */
  lede: "clamp(19px,2.3vw,24px)",
  /** The hero subhead. 17px → 20px. */
  heroBody: "clamp(17px,2vw,20px)",
  /** Newsletter heading in the footer. 20px → 26px. */
  footerLede: "clamp(20px,2.4vw,26px)",
} as const;

/** Fluid spacing, again as the comp's clamp() expressions. */
export const space = {
  /** Horizontal page gutter, used on every 1200px wrapper. */
  gutter: "clamp(20px,4vw,48px)",
  /** Vertical rhythm between major sections. */
  sectionY: "clamp(56px,8vw,100px)",
  /** The shorter section padding (promise strip). */
  sectionYTight: "clamp(44px,6vw,76px)",
  /** Grid gap between bike cards, row / column. */
  gridRow: "clamp(20px,3vw,36px)",
  gridCol: "clamp(18px,2.5vw,28px)",
} as const;

/** The one content width on the site. Measured on 10 separate wrappers. */
export const maxWidth = "1200px";

export const radius = {
  /** Mono placeholder labels + the `empty` badge. */
  chip: "3px",
  /** Every button. */
  button: "4px",
  /** Bike card image, the shop-band photo. */
  card: "6px",
  /** The savings band, testimonial cards, the visit card. */
  panel: "8px",
} as const;

/**
 * The comp's signature button shadow is a hard offset with no blur — it reads
 * as a printed/letterpress edge, and it is what makes the press interaction
 * (translateY + shrinking shadow) legible.
 */
export const shadow = {
  buttonGold: "0 4px 0 rgba(19,26,44,.28)",
  buttonGoldHover: "0 6px 0 rgba(19,26,44,.28)",
  buttonGoldActive: "0 1px 0 rgba(19,26,44,.28)",
  buttonBlue: "0 4px 0 rgba(19,26,44,.22)",
  buttonBlueHover: "0 6px 0 rgba(19,26,44,.22)",
  buttonBlueActive: "0 1px 0 rgba(19,26,44,.22)",
  card: "0 1px 0 #EDF1F8",
  cardHover: "0 14px 32px rgba(19,26,44,.12)",
  bikeCardHover: "0 14px 36px rgba(19,26,44,.16)",
  visitCard: "0 8px 40px rgba(19,26,44,.18)",
} as const;

export const motion = {
  /** The comp's one easing curve. */
  ease: "cubic-bezier(.2,.7,.2,1)",
  /** Standard interactive transition, verbatim from the comp. */
  interactive:
    "transform .22s cubic-bezier(.2,.7,.2,1), background-color .22s ease, border-color .22s ease, box-shadow .22s cubic-bezier(.2,.7,.2,1), color .22s ease",
  /** The slower one used on bike cards. */
  card:
    "transform .45s cubic-bezier(.2,.7,.2,1), box-shadow .45s cubic-bezier(.2,.7,.2,1)",
} as const;

/**
 * Placeholder photography treatment, ported from the comp.
 *
 * Real product photography is being sourced in a parallel session
 * (docs/handoffs/SESSION-2-PHOTOGRAPHY.md). Until it lands, every image slot
 * renders this diagonal hatch plus a mono label describing the shot that
 * belongs there — which is exactly what the comp does, so the slots are
 * pixel-comparable today and swap to real files later.
 */
export const hatch = {
  light: `repeating-linear-gradient(45deg,${color.surface},${color.surface} 12px,${color.hatchLight} 12px,${color.hatchLight} 24px)`,
  overlay: `repeating-linear-gradient(45deg,${color.hatchOverlayLight},${color.hatchOverlayLight} 12px,${color.hatchOverlayDark} 12px,${color.hatchOverlayDark} 24px)`,
  blue: `repeating-linear-gradient(45deg,${color.hatchBlueLight},${color.hatchBlueLight} 14px,${color.hatchBlueDark} 14px,${color.hatchBlueDark} 28px)`,
} as const;
